import { Chart, UpdateMode } from 'chart.js';
import { STATS } from 'state/stats/type';
import {
  getMetricValueFromPayType,
  formatValueToDisplayStr,
  getChartLegendPercentShares,
} from 'utils/chart';

const getOrCreateLegendList = (chart: Chart, id: string) => {
  const legendContainer = document.getElementById(id);
  let listContainer = legendContainer?.querySelector('ul');

  if (!listContainer) {
    listContainer = document.createElement('ul');
    listContainer.style.display = 'flex';
    listContainer.style.flexDirection = 'row';
    listContainer.style.margin = '0';
    listContainer.style.padding = '0';
    listContainer.style.justifyContent = 'flex-end';
    listContainer.style.gap = '32px';
    listContainer.style.marginTop = '24px';

    legendContainer?.appendChild(listContainer);
  }

  return listContainer;
};

export const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate: (
    chart: Chart,
    _: { mode: UpdateMode },
    options: Record<string, any>
  ) => {
    const ul = getOrCreateLegendList(chart, 'legend-container');

    // Remove old legend items
    while (ul?.firstChild) {
      ul?.firstChild.remove();
    }

    // Reuse the built-in legendItems generator
    if (!chart.options.plugins?.legend?.labels?.generateLabels) return;

    const items = chart.options.plugins?.legend?.labels?.generateLabels(chart);

    const summary = chart.data.datasets[0]?.summary;
    const activeTab = chart.data.datasets[0]?.activeSummaryTab;
    const hasMultipleCharts = chart.data.datasets.length > 1;
    const legendPercentShares = getChartLegendPercentShares(activeTab, summary);

    // Create an array of legend items with their associated legendLabelValue
    const legendItemsWithValues = items.map((item) => {
      const datasetIndex = item.datasetIndex || 0;
      const chartDataset = chart.data.datasets[datasetIndex];
      const paymentType = chartDataset?.paymentType;

      const summaryData = summary?.filter(
        (summ) => summ.payment_type === paymentType
      )?.[0];
      const legendLabelValue = getMetricValueFromPayType(
        activeTab,
        summaryData?.current?.gross_volume,
        summaryData?.current?.payments
      );
      const legendPercentShare = legendPercentShares[paymentType] || 0; // Lookup percentage by payment_type

      return {
        item,
        legendLabelValue,
        legendPercentShare,
        chartDataset,
      };
    });

    // Sort the items in descending order of legendLabelValue
    legendItemsWithValues.sort(
      (a, b) => b.legendLabelValue - a.legendLabelValue
    );
    legendItemsWithValues.forEach(
      ({ item, legendLabelValue, legendPercentShare, chartDataset }) => {
        const datasetIndex = item.datasetIndex || 0;

        const li = document.createElement('li');
        li.style.alignItems = 'center';
        li.style.cursor = hasMultipleCharts ? 'pointer' : 'default';
        li.style.display = 'flex';
        li.style.flexDirection = 'row';
        li.style.gap = '6px';

        // Percent value box
        const percentBox = document.createElement('span');
        percentBox.style.height = '24px';
        percentBox.style.backgroundColor =
          chartDataset?.legendBgColor as string;
        percentBox.style.color = chartDataset?.legendTextColor as string;
        percentBox.style.padding = '4px 6px';
        percentBox.style.borderRadius = '4px';
        percentBox.style.fontSize = '12px';
        percentBox.style.fontWeight = '500';
        percentBox.style.lineHeight = '16px';
        percentBox.style.display = 'inline-block';
        percentBox.textContent = `${legendPercentShare}%`;

        // Text label and value
        const labelText = `${item.text}: ${formatValueToDisplayStr(
          activeTab,
          legendLabelValue
        )}`;
        const labelContainer = document.createElement('span');
        labelContainer.style.color = '#4D5664';
        labelContainer.style.fontSize = '12px';
        labelContainer.style.fontWeight = '500';
        labelContainer.style.lineHeight = '16px';
        labelContainer.textContent = labelText;

        // Check if we are in single chart display mode (only one dataset visible)
        const visibleDatasets = chart.data.datasets.filter((dataset, index) =>
          chart.isDatasetVisible(index)
        );
        const isSingleChartSelected = visibleDatasets.length === 1;

        if (isSingleChartSelected && hasMultipleCharts) {
          if (chart.isDatasetVisible(datasetIndex)) {
            labelContainer.style.textDecoration = 'underline'; // underline selected chart legend
          } else {
            labelContainer.style.color = '#999EA7'; // Gray out the hidden datasets
            labelContainer.style.textDecoration = 'none';
          }
        }

        li.addEventListener('click', () => {
          if (!hasMultipleCharts) return;

          const onlyOneVisible = chart.data.datasets.every((dataset, index) =>
            index === datasetIndex
              ? chart.isDatasetVisible(index)
              : !chart.isDatasetVisible(index)
          );

          if (onlyOneVisible) {
            chart.data.datasets.forEach((arg, index) => {
              chart.setDatasetVisibility(index, true);
            });
          } else {
            chart.data.datasets.forEach((arg, index) => {
              if (index === datasetIndex) {
                chart.setDatasetVisibility(index, true);
              } else {
                chart.setDatasetVisibility(index, false);
              }
            });
          }

          chart.update();
        });

        li.appendChild(percentBox);
        li.appendChild(labelContainer);

        ul?.appendChild(li);
      }
    );
  },
};
